import React, { createContext, useEffect, useMemo, useState } from 'react';
import { differenceInMonths } from 'date-fns/differenceInMonths';
import { format } from 'date-fns/format';
import { parseISO } from 'date-fns/parseISO';
import { startOfMonth } from 'date-fns/startOfMonth';
import { defaultToday, defaultMaxDate, DEFAULT_MAX_MONTH_INDEX } from '../constants/dates';
import useCalendarForm from '../useCalendarForm';
import buildMonths from '../utils/buildMonths';
export const CalendarContext = createContext(null);
function CalendarProvider({
  boxShadow = true,
  children,
  dayWithRoundedCorners = true,
  displayYear = true,
  locale,
  maxDate = defaultMaxDate,
  minDate = defaultToday,
  monthsToShow = 2,
  monthWidth = 350,
  scrollDate,
  startDate,
  useDefaultFooter,
  onDismiss,
  onDisplayMonthsChange = () => {},
  ...props
}) {
  const {
    handleKeyDismiss,
    ...calendarFormResponse
  } = useCalendarForm({
    ...props,
    maxDate,
    minDate,
    scrollDate,
    startDate,
    onDismiss
  });
  useEffect(() => {
    document.addEventListener('keydown', handleKeyDismiss);
    return () => {
      document.removeEventListener('keydown', handleKeyDismiss);
    };
  }, [handleKeyDismiss]);
  const scrollMonth = scrollDate || startDate;
  const scrollToMonth = scrollMonth ? differenceInMonths(parseISO(scrollMonth.toString()), startOfMonth(parseISO(minDate.toString()))) : 0;
  const [monthIndex, setMonthIndex] = useState(scrollToMonth);
  const maxMonthIndex = useMemo(() => {
    return differenceInMonths(parseISO(maxDate.toString()), parseISO(minDate.toString())) || DEFAULT_MAX_MONTH_INDEX;
  }, [minDate, maxDate]);
  const months = useMemo(() => {
    return buildMonths(minDate, maxDate, locale);
  }, [minDate, maxDate, locale]);
  const firstDayOfMonth = new Date(months[0].year, months[0].monthIndex, 1);
  const firstValidMonth = format(firstDayOfMonth, 'yyyyMM');
  const startMonth = parseISO(firstValidMonth);
  const context = {
    ...props,
    ...calendarFormResponse,
    boxShadow,
    dayWithRoundedCorners,
    displayYear,
    firstValidMonth,
    locale,
    maxMonthIndex,
    months,
    monthIndex,
    monthsLength: months.length,
    monthsToShow,
    monthWidth,
    scrollToMonth,
    startMonth,
    useDefaultFooter,
    handleKeyDismiss,
    onDismiss,
    onDisplayMonthsChange,
    setMonthIndex
  };
  return React.createElement(CalendarContext.Provider, {
    value: context
  }, children);
}
export default CalendarProvider;